import api from './api';

export type MeteoIconKey =
  | 'clear'
  | 'mainly_clear'
  | 'partly_cloudy'
  | 'overcast'
  | 'fog'
  | 'drizzle'
  | 'rain'
  | 'snow'
  | 'showers'
  | 'snow_showers'
  | 'thunderstorm'
  | 'unknown';

export interface MeteoLocation {
  name: string;
  admin1?: string | null;
  country?: string | null;
  country_code?: string | null;
  lat: number;
  lon: number;
  display: string;
  timezone?: string | null;
}

export interface MeteoCurrent {
  temperature_c: number | null;
  feels_like_c: number | null;
  humidity: number | null;
  weather_code: number | null;
  icon: MeteoIconKey;
  label_key: string;
  is_day: boolean;
  wind_kmh: number | null;
  wind_dir_deg: number | null;
  precip_mm: number | null;
  cloud_cover: number | null;
  pressure_hpa: number | null;
}

export interface MeteoDaily {
  date: string;
  tmin_c: number | null;
  tmax_c: number | null;
  weather_code: number | null;
  icon: MeteoIconKey;
  label_key: string;
  precip_mm: number | null;
  precip_prob: number | null;
  wind_kmh_max: number | null;
  sunrise?: string | null;
  sunset?: string | null;
}

export interface MeteoHourly {
  time: string;
  temperature_c: number | null;
  feels_like_c: number | null;
  humidity: number | null;
  weather_code: number | null;
  icon: MeteoIconKey;
  label_key: string;
  precip_mm: number | null;
  precip_prob: number | null;
  wind_kmh: number | null;
  is_day: boolean;
}

export interface WeatherPayload {
  configured: boolean;
  updated_at?: string;
  stale?: boolean;
  forecast_days?: number;
  location?: MeteoLocation;
  current?: MeteoCurrent | null;
  hourly?: MeteoHourly[];
  daily?: MeteoDaily[];
  source?: string;
  attribution?: string;
  attribution_url?: string;
  error?: string;
}

export interface GeocodeResult {
  name: string;
  admin1?: string | null;
  country?: string | null;
  country_code?: string | null;
  lat: number;
  lon: number;
  timezone?: string | null;
  display: string;
  population?: number | null;
}

export async function getWeather(opts?: {
  forecastDays?: 7 | 16;
  force?: boolean;
}): Promise<WeatherPayload> {
  const params: Record<string, string | number | boolean> = {
    forecast_days: opts?.forecastDays ?? 16,
  };
  if (opts?.force) params.force = 1;
  const response = await api.get('/weather', { params });
  return response.data;
}

export async function getMeteoLocation(): Promise<{
  success: boolean;
  configured: boolean;
  location: MeteoLocation | null;
}> {
  const response = await api.get('/meteo/location');
  return response.data;
}

export async function updateMeteoLocation(location: {
  name: string;
  admin1?: string | null;
  country?: string | null;
  country_code?: string | null;
  lat: number;
  lon: number;
  display?: string;
  timezone?: string | null;
}): Promise<{ success: boolean; location: MeteoLocation }> {
  const response = await api.put('/meteo/location', location);
  return response.data;
}

export async function clearMeteoLocation(): Promise<{ success: boolean }> {
  const response = await api.delete('/meteo/location');
  return response.data;
}

export async function searchMeteoPlaces(
  q: string,
  language = 'es'
): Promise<GeocodeResult[]> {
  const response = await api.get('/meteo/geocode', {
    params: { q, language },
  });
  return response.data?.results || [];
}

export function formatTempC(
  value: number | null | undefined,
  _locale = 'es'
): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Math.round(Number(value))}°C`;
}
